// ParameterDashboard.js
import React, { useState, useEffect } from "react";
import "./ParameterDashboard.css";
import {
  ArrowLeft,
  Plus,
  Save,
  Pencil,
  Trash2,
  ArrowRight,
} from "lucide-react";

import AllKeywordsModal from "../ModelUI/AllKeywordsModal/AllKeywordsModal";
import AddSubParameterModal from "../ModelUI/AddSubParameterModal/AddSubParameterModal";
import ConfirmDeleteModal from "../ModelUI/ConfirmDeleteModal/ConfirmDeleteModal";
import AddSectionModal from "../ModelUI/AddSectionModal/AddSectionModal";
import EditSectionModal from "../ModelUI/EditSectionModal/EditSectionModal";
import KeywordInput from "../KeywordInput/KeywordInput";
import SaveAsTemplateModal from "../ModelUI/SaveAsTemplateModal/SaveAsTemplateModal";
import TemplateCard from "./TemplateCard";
import ApiService from "../../services/Api.service";
import TemplateDetailView from "./TemplateDetailView";

const ParameterDashboard = () => {
  const [templates, setTemplates] = useState([]);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [openSections, setOpenSections] = useState([]);
  const [openParams, setOpenParams] = useState({});
  const [showAllKeywords, setShowAllKeywords] = useState(false);
  const [showAddSubParamModal, setShowAddSubParamModal] = useState(false);
  const [selectedSectionIndex, setSelectedSectionIndex] = useState(null);
  const [editingParam, setEditingParam] = useState({ sectionIndex: null, paramIndex: null });
  const [originalKeywords, setOriginalKeywords] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState({ sectionIndex: null, paramIndex: null });
  const [showAddSectionModal, setShowAddSectionModal] = useState(false);
  const [keywords, setKeywords] = useState([]);
  const [showDeleteSectionModal, setShowDeleteSectionModal] = useState(false);
  const [deleteSectionIndex, setDeleteSectionIndex] = useState(null);
  const [editingSectionIndex, setEditingSectionIndex] = useState(null);
  const [newSectionName, setNewSectionName] = useState("");
  const [userTemplates, setUserTemplates] = useState([]);
  const [showRenameModal, setShowRenameModal] = useState(false);
  const [deleteTemplateIndex, setDeleteTemplateIndex] = useState(null);
  const [showDeleteTemplateModal, setShowDeleteTemplateModal] = useState(false);
  const [editDefaultConfirmation, setEditDefaultConfirmation] = useState(false);
  const [editingKeywords, setEditingKeywords] = useState([]); // 🔹 new dedicated state
  const [templateError, setTemplateError] = useState(""); // 🔹 new dedicated state
  const [editingWeightCount, setEditingWeightCount] = useState(1);
  const [editIsPartial, setEditIsPartial] = useState(false);
  const [editPartialWeight, setEditPartialWeight] = useState(0);
  const [error, setError] = useState("");
  const [showReportEditHeader ,setShowReportEditHeader] = useState(false)
  const [showAddProductKeyword, setShowAddProductKeyword] = useState(false)

  const normalizeTemplates = (templatesList) => {
    if (!Array.isArray(templatesList)) return [];
    return templatesList.map((tpl) => ({
      ...tpl,
      name: tpl.name || tpl.title || "",
      sections: (tpl.sections || []).map((sec) => ({
        ...sec,
        title: sec.title || sec.name || "",
        is_fatal: sec.is_fatal ?? false,
        parameters: (sec.parameters || []).map((param) => ({
          ...param,
          title: param.title || param.name || "",
          default_confirmation: param.default_confirmation ?? false,
          weight_count: param.weight_count ?? 1,
          is_partial: param.is_partial ?? false,
          partial_weight: param.partial_weight ?? 0,
        })),
      })),
    }));
  };

  const handleDeleteParameter = (sectionIndex, paramIndex) => {
    setDeleteTarget({ sectionIndex, paramIndex });
    setShowDeleteModal(true);
  };

  const handleAddSection = (sectionName) => {
    const updatedTemplate = { ...selectedTemplate };
    const exists = (updatedTemplate.sections || []).some(
      (sec) => (sec.title || sec.name || "").toLowerCase() === sectionName.toLowerCase()
    );

    if (exists) {
      alert("Section with this name already exists!");
      return;
    }

    if (!updatedTemplate.sections) updatedTemplate.sections = [];
    updatedTemplate.sections.push({
      title: sectionName,
      parameters: [],
      is_fatal: false,
    });

    setSelectedTemplate(updatedTemplate);
    setShowAddSectionModal(false);
  };

  const fetchDetails = async () => {
    try {
      let subprocessRes = await ApiService.getPresetTemplates();
      if (subprocessRes.status === 200 && subprocessRes.data?.presets) {
        setTemplates(normalizeTemplates(subprocessRes.data.presets));
      } else {
        setTemplates([]);
      }

      let templateRes = await ApiService.getTemplates();
      if (templateRes.status === 200 && templateRes.data?.data) {
        setUserTemplates(normalizeTemplates(templateRes.data.data));
      }
    } catch (error) {
      console.error("Error fetching subprocesses:", error);
      setTemplates([]);
    }
  };
  useEffect(() => {
    fetchDetails()
  }, []);

  // inside ParameterDashboard.js

  const handleSaveTemplate = async ({ name, process_type, shared_with,is_real_time_transcription }) => {
    try {
      const copiedTemplate = {
        ...selectedTemplate,
        name,
        process_type,
        shared_with,
        is_real_time_transcription,
        sections: selectedTemplate.sections.map((section) => ({
          ...section,
          is_fatal: section.is_fatal ?? false,
          parameters: section.parameters.map((param) => {
            let default_confirmation =
              typeof param.default_confirmation === "boolean"
                ? param.default_confirmation
                : false;

            if (param.default_confirmation === undefined) {
              if (param.title.toLowerCase().includes("resolved")) {
                default_confirmation = true;
              } else if (param.title.toLowerCase().includes("empathy")) {
                default_confirmation = false;
              }
            }

            return {
              ...param,
              default_confirmation,
              weight_count: param.weight_count ?? 1, // ✅ send weight_count to API
              is_partial: param.is_partial ?? false,
              partial_weight: param.partial_weight ?? 0,
            };
          }),
        })),
      };

      let response;
      if (copiedTemplate.id && selectedTemplate?.type !== "preset") {
        response = await ApiService.updateTemplate(
          copiedTemplate.id,
          copiedTemplate
        );
      } else {
        const payloadToCreate = { ...copiedTemplate };
        delete payloadToCreate.id;
        delete payloadToCreate._id;
        response = await ApiService.addTemplate(payloadToCreate);
        copiedTemplate.id = response.data?.id || response.data?.template_id;
      }

      setUserTemplates((prev) => {
        const filtered = prev.filter((t) => t.id !== copiedTemplate.id);
        return [...filtered, copiedTemplate];
      });

      setSelectedTemplate(null);
      setShowRenameModal(false);
      setTemplateError("");
    } catch (err) {
      let errorMessage = "Something went wrong";
      if (err.response?.data?.detail) {
        errorMessage = err.response.data.detail;
      } else if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }
      setTemplateError(errorMessage);
    }
  };


  const confirmDelete = () => {
    const updatedTemplate = { ...selectedTemplate };
    updatedTemplate.sections[deleteTarget.sectionIndex].parameters.splice(deleteTarget.paramIndex, 1);
    setSelectedTemplate(updatedTemplate);
    setShowDeleteModal(false);
  };

  const handleAllKeywords = (keywords) => {
    setShowAllKeywords(true);
    setKeywords(keywords);
  };

  const handleCancelEdit = () => {
    if (editingParam.sectionIndex !== null && editingParam.paramIndex !== null) {
      const updatedTemplate = { ...selectedTemplate };
      updatedTemplate.sections[editingParam.sectionIndex].parameters[
        editingParam.paramIndex
      ].keywords = originalKeywords;
      setSelectedTemplate(updatedTemplate);
    }
    setEditingParam({ sectionIndex: null, paramIndex: null });
    setEditingKeywords([]); // reset
  };


  const handleBack = () => {
    setSelectedTemplate(null);
    setShowReportEditHeader(false)
    setShowAddProductKeyword(false)
    setOpenSections([]);
    setOpenParams({});
    setEditingParam({ sectionIndex: null, paramIndex: null });
  };

  const toggleSection = (index) => {
    setOpenSections((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
    setEditingParam({ sectionIndex: null, paramIndex: null });
  };

  const toggleParam = (sectionIndex, paramIndex) => {
    setOpenParams((prev) => {
      const current = prev[sectionIndex] || [];
      const isOpen = current.includes(paramIndex);
      return {
        ...prev,
        [sectionIndex]: isOpen
          ? current.filter((i) => i !== paramIndex)
          : [...current, paramIndex],
      };
    });
  };

  const handleAddParameter = (sectionIndex) => {
    setSelectedSectionIndex(sectionIndex);
    setShowAddSubParamModal(true);
  };

  const handleSubParamAdd = (paramTitle, keywords) => {
    const updatedTemplate = { ...selectedTemplate };

    const section = updatedTemplate.sections[selectedSectionIndex];

    // 🔥 Check duplicate in parameters (case insensitive)
    const exists = section.parameters.some(
      (param) => param.title.toLowerCase() === paramTitle.toLowerCase()
    );

    if (exists) {
      alert("Parameter with this name already exists in this section!");
      return;
    }

    // Rule-based default confirmation
    let defaultConfirmation = false;
    if (paramTitle.toLowerCase().includes("resolved")) {
      defaultConfirmation = true;
    } else if (paramTitle.toLowerCase().includes("empathy")) {
      defaultConfirmation = false;
    }

    section.parameters.push({
      title: paramTitle,
      keywords,
      default_confirmation: defaultConfirmation,
      weight_count: 1, // ✅ default value
      is_partial: false,
      partial_weight: 0,
    });


    setSelectedTemplate(updatedTemplate);
    setShowAddSubParamModal(false);
  };

  const handleOpenTemplate = async (template) => {
    if (!template) return;
    const templateId = template.id || template._id;
    if (templateId && template.type !== "builder") {
      try {
        const response = await ApiService.getTemplateById(templateId);
        if (response.status === 200 && response.data) {
          const fetchedTpl = response.data.template || response.data.data || response.data;
          const normalized = normalizeTemplates([fetchedTpl])[0] || fetchedTpl;
          setSelectedTemplate(normalized);
          return;
        }
      } catch (err) {
        console.error("Failed to fetch template by ID:", err);
      }
    }
    setSelectedTemplate(normalizeTemplates([template])[0] || template);
  };

  const handleCopyTemplate = async (template) => {
    const templateId = template?.id || template?._id;
    if (templateId) {
      try {
        const response = await ApiService.duplicateTemplate(templateId);
        if (response.status === 200) {
          await fetchDetails();
          return;
        }
      } catch (err) {
        console.error("Error duplicating template via API:", err);
        const errorDetail = err.response?.data?.detail || err.response?.data?.message;
        if (errorDetail) {
          alert(`Error duplicating template: ${errorDetail}`);
          return;
        }
      }
    }

    // Fallback: local clone and open in editor
    let tplToCopy = template;
    if (templateId) {
      try {
        const response = await ApiService.getTemplateById(templateId);
        if (response.status === 200 && response.data) {
          tplToCopy = response.data.template || response.data.data || response.data;
        }
      } catch (err) {
        console.error("Error fetching template for copy:", err);
      }
    }
    const copiedTemplate = JSON.parse(JSON.stringify(tplToCopy));

    // Remove ID so it behaves like a new one
    delete copiedTemplate.id;
    delete copiedTemplate._id;

    // Rename to avoid conflicts
    copiedTemplate.name = `${template.name} (Copy)`;

    // Open in editor
    setSelectedTemplate(normalizeTemplates([copiedTemplate])[0] || copiedTemplate);
  };

  const handleSaveEdit = () => {
    setEditingParam({ sectionIndex: null, paramIndex: null });
  };

  const handleEditReportHeader = async (item) => {
    setShowReportEditHeader(true);
    await handleOpenTemplate(item);
  };

  const handleAddProductKeyword = async (item) => {
    setShowAddProductKeyword(true);
    await handleOpenTemplate(item);
  };

  // 🔥 Instead of overwriting, open Rename Modal
  const handleSaveChanges = () => {
    setShowRenameModal(true);
    fetchDetails()
  };

  // Delete Section
  const handleDeleteSection = (sectionIndex) => {
    setDeleteSectionIndex(sectionIndex);
    setShowDeleteSectionModal(true);
  };

  const confirmDeleteSection = () => {
    const updatedTemplate = { ...selectedTemplate };
    updatedTemplate.sections.splice(deleteSectionIndex, 1);
    setSelectedTemplate(updatedTemplate);
    setShowDeleteSectionModal(false);
    setDeleteSectionIndex(null);
  };

  // Edit Section
  const handleEditSection = (sectionIndex) => {
    setEditingSectionIndex(sectionIndex);
  };

  const saveEditedSection = (updatedName, isFatalValue) => {
    const updatedTemplate = { ...selectedTemplate };
    updatedTemplate.sections[editingSectionIndex].title = updatedName;
    updatedTemplate.sections[editingSectionIndex].is_fatal = isFatalValue;
    setSelectedTemplate(updatedTemplate);
    setEditingSectionIndex(null);
  };
  const handleDeleteTemplate = async (idx) => {
    try {
      const templateToDelete = userTemplates[idx];

      if (!templateToDelete?.id) {
        console.error("Template has no ID:", templateToDelete);
        return;
      }

      // 🔥 Call backend API
      const response = await ApiService.deleteTemplate(templateToDelete.id);
      if (response.status !== 200) {
        console.error("Failed to delete template:", response);
        return;
      }
      // Remove from state only if API succeeds
      setUserTemplates((prev) => prev.filter((_, i) => i !== idx));

    } catch (error) {
      console.error("Error deleting template:", error);
    }
  };
  const handleDeleteTemplateClick = (idx) => {
    setDeleteTemplateIndex(idx);
    setShowDeleteTemplateModal(true);
  };

  const confirmDeleteTemplate = async () => {
    if (deleteTemplateIndex !== null) {
      await handleDeleteTemplate(deleteTemplateIndex);
      setDeleteTemplateIndex(null);
      setShowDeleteTemplateModal(false);
    }
  };


  return (
    <div className="apt-page">

      {!selectedTemplate ? (
        <>
          <div className="apt-content">
            <div>
              <h5>Preset Templates</h5>
              <div className="apt-card-container" >
                {templates?.map((item, idx) => (
                  <div
                    className="apt-card"
                    key={idx}
                    onClick={() => handleOpenTemplate(item)}
                  >
                    <div className="apt-card-header">
                      <h3 className="apt-card-title">{item.name}</h3>
                      <div className="apt-icon">{item.icon === "Arrow" ? (<ArrowRight size={24} />) : (<Plus size={24} />)}</div>
                    </div>
                    <p className="apt-description">{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h5>My Templates</h5>
              <div className="apt-card-container">
                {userTemplates.length === 0 ? (
                  <TemplateCard />
                ) : (
                  userTemplates.map((item, idx) => (
                    <TemplateCard
                      key={idx}
                      name={item.name}
                      parameters={item.sections?.length || 0}
                      type={item.process_type}
                      onClick={() => handleOpenTemplate(item)}
                      onDelete={() => handleDeleteTemplateClick(idx)}
                      onEdit={() => handleOpenTemplate(item)}
                      onCopy={() => handleCopyTemplate(item)} 
                      onEditReport = {() => handleEditReportHeader(item)}
                      onAddProductKeyword={() => handleAddProductKeyword(item)}
                      isAddProductKeyword={item.transcription_accuracy}
                    />
                  ))
                )}
              </div>


            </div>
          </div>
        </>
      ) : (
        <TemplateDetailView
          selectedTemplate={selectedTemplate}
          handleBack={handleBack}
          handleSaveChanges={handleSaveChanges}
          onAddSection={() => setShowAddSectionModal(true)}
        >
          {/* Sections content */}
          <div className="template-sections">
            {selectedTemplate.sections.map((section, i) => (
              <div className="template-section-block" key={i}>
                <div className="template-section-header" onClick={() => toggleSection(i)}>
                    <>
                      <span>
                        {i + 1}. {section.title}
                        {section.is_fatal && (
                          <span
                            style={{
                              marginLeft: "10px",
                              backgroundColor: "#fee2e2",
                              color: "#ef4444",
                              fontSize: "11px",
                              padding: "2px 8px",
                              borderRadius: "4px",
                              fontWeight: "600",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px"
                            }}
                          >
                            Fatal
                          </span>
                        )}
                      </span>
                      <div className="section-actions">
                        <button
                          className="edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditSection(i);
                          }}
                        >
                          <Pencil size={14} /> Edit
                        </button>
                        <button
                          className="delete-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSection(i);
                          }}
                        >
                          <Trash2 size={14} />
                        </button>
                        <button
                          className="add-new-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddParameter(i);
                          }}
                        >
                          <Plus size={14} style={{ marginRight: "4px" }} />
                          Add New
                        </button>
                      </div>
                    </>
                </div>


                {openSections.includes(i) &&
                  section.parameters.map((param, j) => {
                    const isParamOpen = openParams[i]?.includes(j);
                    const isEditing =
                      editingParam.sectionIndex === i &&
                      editingParam.paramIndex === j;


                    return (
                      <div className="parameter-box" key={j}>
                        <div className="parameter-top">
                          <div
                            className="param-title-wrapper"
                            onClick={() => toggleParam(i, j)}
                            style={{ cursor: "pointer" }}
                          >
                            <div className="param-text">{param.title}</div>
                            {param.keywords?.length > 0 && (
                              <span className="param-count">{param.keywords.length} keywords</span>
                            )}
                          </div>

                          <div className="param-actions">
                            {isEditing ? (
                              <>
                                <button
                                  className="section-cancel-btn"
                                  onClick={handleCancelEdit}
                                >
                                  Cancel
                                </button>
                                <button
                                  className="save-btn"
                                  onClick={() => {
                                    const updatedTemplate = { ...selectedTemplate };
                                    const editingParamRef =
                                      updatedTemplate.sections[editingParam.sectionIndex].parameters[
                                      editingParam.paramIndex
                                      ];

                                    editingParamRef.keywords = [...editingKeywords];
                                    editingParamRef.default_confirmation = editDefaultConfirmation;
                                    editingParamRef.weight_count = editingWeightCount;
                                    editingParamRef.is_partial = editIsPartial;
                                    editingParamRef.partial_weight = editPartialWeight;
                                    setSelectedTemplate(updatedTemplate);
                                    handleSaveEdit();
                                    setEditingParam({ sectionIndex: null, paramIndex: null });
                                    setEditingKeywords([]);
                                  }}
                                >
                                  <Save size={16} style={{ marginRight: "6px" }} /> Save
                                </button>

                              </>
                            ) : (
                              <button
                                className="edit-btn"
                                onClick={() => {
                                  setEditingParam({ sectionIndex: i, paramIndex: j });
                                  setOriginalKeywords(param.keywords);
                                  setEditingKeywords([...param.keywords]);
                                  setEditDefaultConfirmation(param.default_confirmation || false);
                                  setEditingWeightCount(param.weight_count || 1);
                                  setEditIsPartial(param.is_partial || false);
                                  setEditPartialWeight(param.partial_weight || 0);
                                  if (!isParamOpen) toggleParam(i, j);
                                }}
                              >
                                <Pencil size={14} /> Edit
                              </button>


                            )}

                            <button
                              className="delete-btn"
                              onClick={() => handleDeleteParameter(i, j)}
                            >
                              <Trash2 size={14} />
                            </button>
                          </div>
                        </div>

                        {isParamOpen && (
                          <div className="keywords-box">
                            <div className="keywords-header">
                              <span>Used Keywords</span>
                              {isEditing && (
                                <span
                                  className="all-keywords-link"
                                  onClick={() => handleAllKeywords(param.keywords)}
                                >
                                  Add Keyword
                                </span>
                              )}
                            </div>

                            <div className="keywords-list">
                              {isEditing ? (
                                <>
                                  <KeywordInput
                                    keywords={editingKeywords}
                                    onChange={(updated) => setEditingKeywords(updated)}
                                  />
                                    <div
                                      className="default-confirmation-checkbox"
                                      style={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: "1rem",
                                        marginTop: "0.75rem",
                                        flexWrap: "wrap",
                                      }}
                                    >
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", marginBottom: 0, whiteSpace: "nowrap", cursor: "pointer" }}>
                                          <input
                                            type="checkbox"
                                            checked={editDefaultConfirmation}
                                            onChange={(e) => setEditDefaultConfirmation(e.target.checked)}
                                          />
                                          Default Confirmation
                                        </label>
                                        <span
                                          style={{
                                            fontSize: "0.75rem",
                                            color: editDefaultConfirmation ? "#28a745" : "#ff9800",
                                            whiteSpace: "nowrap",
                                          }}
                                        >
                                          {editDefaultConfirmation
                                            ? "The answer 'Yes' will always appear in the final report."
                                            : "The answer will be generated automatically by AI in the final report."}
                                        </span>
                                      </div>

                                      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                                        <label style={{ fontSize: "0.8125rem", marginBottom: 0, whiteSpace: "nowrap" }}>Weight</label>
                                        <input
                                          type="number"
                                          value={editingWeightCount}
                                          min={1}
                                          onChange={(e) => {
                                            const value = e.target.value;

                                            if (value === "") {
                                              setEditingWeightCount("");
                                              setError("Please enter a number");
                                            } else if (Number(value) < 1) {
                                              setEditingWeightCount(value);
                                              setError("Value must be at least 1");
                                            } else {
                                              setEditingWeightCount(Number(value));
                                              setError("");
                                            }
                                          }}
                                          onBlur={(e) => {
                                            if (e.target.value === "" || Number(e.target.value) < 1) {
                                              setEditingWeightCount(1);
                                              setError("");
                                            }
                                          }}
                                          style={{ width: "3.25rem", height: "1.75rem", fontSize: "0.8125rem", padding: "0 0.375rem", borderRadius: "0.25rem", border: "0.0625rem solid #cbd5e1", textAlign: "center" }}
                                        />
                                        {error && <span style={{ color: "#ef4444", fontSize: "0.75rem", whiteSpace: "nowrap" }}>{error}</span>}
                                      </div>

                                      <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                                        <label style={{ display: "flex", alignItems: "center", gap: "0.375rem", fontSize: "0.8125rem", marginBottom: 0, whiteSpace: "nowrap", cursor: "pointer" }}>
                                          <input
                                            type="checkbox"
                                            checked={editIsPartial}
                                            onChange={(e) => setEditIsPartial(e.target.checked)}
                                          />
                                          Is Partial
                                        </label>
                                      </div>

                                      {editIsPartial && (
                                        <div style={{ display: "flex", alignItems: "center", gap: "0.375rem" }}>
                                          <label style={{ fontSize: "0.8125rem", marginBottom: 0, whiteSpace: "nowrap" }}>Partial Weight</label>
                                          <input
                                            type="number"
                                            value={editPartialWeight}
                                            min={0}
                                            onChange={(e) => {
                                              const value = e.target.value;
                                              if (value === "") {
                                                setEditPartialWeight("");
                                              } else {
                                                setEditPartialWeight(Number(value));
                                              }
                                            }}
                                            onBlur={(e) => {
                                              if (e.target.value === "") {
                                                setEditPartialWeight(0);
                                              }
                                            }}
                                            style={{ width: "3.25rem", height: "1.75rem", fontSize: "0.8125rem", padding: "0 0.375rem", borderRadius: "0.25rem", border: "0.0625rem solid #cbd5e1", textAlign: "center" }}
                                          />
                                        </div>
                                      )}
                                    </div>
                                </>
                              ) : (
                                param.keywords.map((kw, idx) => (
                                  <span key={idx} className="keyword-pill">
                                    {kw}
                                  </span>
                                ))
                              )}

                            </div>
                            <div
                              className="default-confirmation-checkbox"
                              style={{
                                marginTop: "6px",
                                fontSize: "12px",
                                color: param.default_confirmation ? "#28a745" : "#ff9800",
                              }}
                            >
                              {isEditing ? (
                                ""
                              ) : (
                                <>
                                  {param.default_confirmation
                                    ? "The answer will be generated automatically by AI in the final report."
                                    : "The answer 'Yes' will always appear in the final report."}

                                  <div style={{ marginTop: "4px", fontWeight: "bold" }}>
                                    Weight {param.weight_count ?? 1}
                                    {param.is_partial && (
                                      <span style={{ marginLeft: "12px", color: "#4f46e5" }}>
                                        | Partial Weight {param.partial_weight ?? 0}
                                      </span>
                                    )}
                                  </div>
                                </>
                              )}
                            </div>

                          </div>
                        )}
                      </div>
                    );
                  })}

              </div>
            ))}

            <div 
              className="add-section-card-row"
              onClick={() => setShowAddSectionModal(true)}
            >
              <div className="add-section-card-content">
                <Plus size={18} />
                <span>Add New Section</span>
              </div>
            </div>
          </div>
        </TemplateDetailView>
      )}

      

      {/* 🔥 Save As / Rename Modal */}
      {showRenameModal && selectedTemplate && (
        <SaveAsTemplateModal
          initialName={selectedTemplate.name || selectedTemplate.title || ""}
          initialType={selectedTemplate.process_type || "Approval"}
          initialRealtime={selectedTemplate.is_real_time_transcription || false}
          onClose={() => (setShowRenameModal(false), setTemplateError(""))}
          onSave={handleSaveTemplate}   // ✅ clean call
          templateError={templateError}
          sharedWith={selectedTemplate.shared_with || []}
          isRealTimeTranscriptionFeature={selectedTemplate.real_time_transcription || false}
          
        />
      )}
      {editingSectionIndex !== null && selectedTemplate && (
        <EditSectionModal
          initialName={selectedTemplate.sections[editingSectionIndex]?.title || ""}
          initialIsFatal={selectedTemplate.sections[editingSectionIndex]?.is_fatal || false}
          onClose={() => setEditingSectionIndex(null)}
          onSave={saveEditedSection}
        />
      )}
      {showAllKeywords && (
        <AllKeywordsModal
          onClose={() => setShowAllKeywords(false)}
          keywords={editingKeywords}
          setKeywords={setEditingKeywords}  // 🔹 update only editingKeywords
        />
      )}


      {showAddSubParamModal && (
        <AddSubParameterModal
          sectionName={selectedTemplate.sections[selectedSectionIndex].title}
          onClose={() => setShowAddSubParamModal(false)}
          onAdd={handleSubParamAdd}
        />
      )}


      {showDeleteModal && (
        <ConfirmDeleteModal
          itemName={
            selectedTemplate.sections[deleteTarget.sectionIndex].parameters[
              deleteTarget.paramIndex
            ]?.title
          }
          onClose={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
        />
      )}

      {showAddSectionModal && (
        <AddSectionModal
          onClose={() => setShowAddSectionModal(false)}
          onAdd={handleAddSection}
        />
      )}
      {showDeleteSectionModal && (
        <ConfirmDeleteModal
          itemName={selectedTemplate.sections[deleteSectionIndex]?.title}
          onClose={() => setShowDeleteSectionModal(false)}
          onConfirm={confirmDeleteSection}
        />
      )}
      {showDeleteTemplateModal && (
        <ConfirmDeleteModal
          itemName={userTemplates[deleteTemplateIndex]?.name}
          onClose={() => setShowDeleteTemplateModal(false)}
          onConfirm={confirmDeleteTemplate}
        />
      )}

    </div>
  );
};

export default ParameterDashboard;
